import React from 'react';
import { POLICIES, POLICY_CONTACT } from '../policyContent';

const { email: EMAIL, whatsapp: WHATSAPP } = POLICY_CONTACT;
const WHATSAPP_URL = `https://wa.me/${WHATSAPP.replace(/\D/g, '')}`;

const linkClass =
  'text-[var(--brand-green)] underline underline-offset-4 decoration-[var(--brand-sage)] hover:text-[#999959] transition-colors break-words';

// Turns the support email / WhatsApp number inside policy copy into links.
const linkify = (text) =>
  text.split(new RegExp(`(${EMAIL.replace(/\./g, '\\.')}|${WHATSAPP.replace('+', '\\+')})`)).map((part, idx) => {
    if (part === EMAIL) {
      return <a key={idx} href={`mailto:${EMAIL}`} className={linkClass}>{part}</a>;
    }
    if (part === WHATSAPP) {
      return <a key={idx} href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>{part}</a>;
    }
    return part;
  });

const bodyClass = 'text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans';

const PolicyBlock = ({ block }) => {
  if (block.p) {
    return <p className={`${bodyClass} mb-5`}>{linkify(block.p)}</p>;
  }

  if (block.h3) {
    return (
      <h3 className="font-serif text-xl sm:text-2xl text-[#1a1a1a] font-normal mt-8 mb-3 leading-snug">
        {block.h3}
      </h3>
    );
  }

  if (block.ul) {
    return (
      <ul className={`${bodyClass} space-y-3 mb-6`}>
        {block.ul.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span className="mt-[0.65em] h-1 w-1 flex-shrink-0 rounded-full bg-[var(--brand-sage)]" aria-hidden="true" />
            <span className="min-w-0">{linkify(item)}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.ol) {
    return (
      <ol className={`${bodyClass} space-y-4 mb-6`}>
        {block.ol.map((item, idx) => (
          <li key={idx} className="flex items-start gap-4">
            <span className="font-serif text-lg leading-snug text-[var(--brand-green-tint-accent)] flex-shrink-0 w-6">
              {String(idx + 1).padStart(2, '0')}
            </span>
            <span className="min-w-0">{linkify(item)}</span>
          </li>
        ))}
      </ol>
    );
  }

  if (block.table) {
    return (
      <div className="mb-8 border border-[#E5E7EB] shadow-sm overflow-x-auto">
        <table className="w-full text-left font-sans border-collapse">
          <thead>
            <tr className="bg-[var(--brand-green-tint-bg)]">
              {block.table.head.map((cell) => (
                <th
                  key={cell}
                  scope="col"
                  className="px-4 py-3 sm:px-6 sm:py-4 text-[11px] font-mono uppercase tracking-wider text-[var(--brand-green)] font-medium align-bottom last:text-right"
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row) => (
              <tr key={row[0]} className="border-t border-[#EEF1E4]">
                {row.map((cell, idx) =>
                  idx === 0 ? (
                    <th key={idx} scope="row" className="px-4 py-4 sm:px-6 text-sm sm:text-base text-[#5a5346] font-normal">
                      {cell}
                    </th>
                  ) : (
                    <td key={idx} className="px-4 py-4 sm:px-6 font-serif text-xl sm:text-2xl text-[#1a1a1a] text-right whitespace-nowrap">
                      {cell}
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.contact) {
    return (
      <div className="mb-6 p-6 sm:p-8 bg-[var(--brand-green-tint-bg)] border border-[var(--brand-green-border)] space-y-4">
        <div>
          <span className="block text-[11px] font-mono uppercase tracking-wider text-[var(--brand-green)] font-medium mb-1">
            Email
          </span>
          <a href={`mailto:${EMAIL}`} className="font-serif text-lg sm:text-xl text-[#1a1a1a] hover:text-[#999959] transition-colors break-words">
            {EMAIL}
          </a>
        </div>
        <div>
          <span className="block text-[11px] font-mono uppercase tracking-wider text-[var(--brand-green)] font-medium mb-1">
            {block.whatsappLabel || 'WhatsApp'}
          </span>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-serif text-lg sm:text-xl text-[#1a1a1a] hover:text-[#999959] transition-colors"
          >
            {WHATSAPP}
          </a>
        </div>
      </div>
    );
  }

  return null;
};

const Policy = ({ slug }) => {
  const policy = POLICIES[slug];

  return (
    <main className="bg-[#FFFFFF] text-[#1a1a1a] selection:bg-[var(--brand-green-tint-bg)] selection:text-[var(--brand-green)] font-sans min-h-screen pb-24">
      {/* Policy Header */}
      <section className="pt-12 pb-10 sm:pt-16 sm:pb-12 border-b border-[#EEF1E4]">
        <div className="max-w-[840px] mx-auto px-6 text-center">
          <span className="text-xs font-mono uppercase tracking-wider text-[#999959] font-medium block mb-3">
            {policy.eyebrow}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight tracking-tight">
            {policy.title}
          </h1>
          <div className="w-12 h-px bg-[var(--brand-sage)] mx-auto mt-6 opacity-60" />
        </div>
      </section>

      {/* Policy Content */}
      <article className="max-w-[760px] mx-auto px-6 pt-10 sm:pt-12">
        {policy.intro.map((text, idx) => (
          <p key={idx} className={`${bodyClass} mb-5`}>
            {linkify(text)}
          </p>
        ))}

        {policy.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a] font-normal mt-12 mb-5 leading-snug">
              {section.heading}
            </h2>
            {section.blocks.map((block, idx) => (
              <PolicyBlock key={idx} block={block} />
            ))}
          </section>
        ))}
      </article>
    </main>
  );
};

export default Policy;
